import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import _ from 'lodash';
import queryString from 'query-string';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import config from '@/configs/config';
import { useProviderDetailsContext } from '../../contexts/providerDetails';
import { useProductsContext } from '../../contexts/products';
import { useSearchProductsContext } from '../../contexts/searchProducts';
import { useProgressContext } from '../../../preorder/contexts/useProgressContext';
import Navmenu from '../navmenu';
import {
  briefSearchProducts,
  findProviderDetails,
} from '../../api/ecDataQueryApi';
import ProductComponent from '../product/ProductComponent';
import ControllerComponent from './ControllerComponent';
import ProviderSideSectionComponent from './ProviderSideSectionComponent';
import ProviderSearcher from './ProviderSearcher';
import IMG from '../img';

const Wrapper = styled.div`
  text-align: center;
`;

const ProviderBanner = styled(IMG)`
  width: 100%;
  margin-left: 0;
  object-fit: contain;
  display: block;
  @media (max-width: 1340px) {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
  }
`;

const ProviderNotification = styled.div`
  width: 100%;
  margin-left: 0;
  object-fit: contain;
  display: block;
  padding: 5px;
  color: ${(props) => props.color || 'white'};
  background-color: ${(props) => props.backgroundColor || 'black'};
  @media (max-width: 1340px) {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
  }
`;

const SearchResultContainer = styled.div`
  display: inline-block;
  min-width: 220px;
  width: calc(100% - 200px);
  vertical-align: top;
  text-align: center;
`;

const SearchProductComponentsContainer = styled.div`
  min-height: 100vh;
  ${(props) => (props.productSelectingMode ? `
    overflow: auto;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: white;
    z-index: 999998;
    &::-webkit-scrollbar{
      display: none;
    }
  ` : '')}
`;

const LoadingMoreDetector = styled.div`
  text-align: center;
  margin: 10px;
`;

export default (props) => {
  const {
    location,
    match,
  } = props;
  const {
    providerDetails: providerDetailsCache,
    addProviderDetails,
  } = useProviderDetailsContext();
  const {
    addProducts,
  } = useProductsContext();
  const {
    searchProductsConditions: searchProductsConditionsCache,
    searchProductsResults: searchProductsResultsCache,
    searchProductsPage: searchProductsPageCache,
    searchProductsHasMore: searchProductsHasMoreCache,
    set: setSearchProductsConditionsAndResultsCache,
  } = useSearchProductsContext();
  const { start: startProgress, end: endProgress } = useProgressContext();
  const loadMoreDetector = useRef(null);
  const searchResultHeadPosition = useRef(null);
  const { t } = useTranslation();

  const provider = useMemo(() => _.get(match, ['params', 'provider'], ''), [match]);
  const searchString = useMemo(() => _.get(location, ['search'], ''), [location]);
  const {
    keys,
    searchable,
    orderBy,
    reverse,
    tags,
    productSelectingMode = false,
  } = useMemo(() => queryString.parse(searchString, { arrayFormat: 'index' }), [searchString]);

  const isSearchingRef = useRef(false);
  const [searchConditions, setSearchConditions] = useState(null);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchResult, setSearchResult] = useState(null);

  useEffect(() => {
    try {
      document.scrollingElement.scrollTo(0, searchResultHeadPosition.current.offsetTop);
    } catch (error) { console.error(error); }
    setSearchResult(null);
    setCurrentPage(1);
    setCanLoadMore(true);

    setSearchConditions({
      ...(provider ? { provider } : {}),
      ...(keys ? { keys: keys.split(',') } : {}),
      ...(searchable ? { searchable } : {}),
      ...(tags ? { tags: tags.split(',') } : {}),
      ...(
        orderBy ? {
          orderBy,
          ...((reverse || '').toLowerCase() === 'true' ? { reverse: true } : {}),
        } : {
        }
      ),
    });
  }, [searchable, provider, reverse, orderBy, tags, keys]);

  useEffect(() => {
    if (
      isSearchingRef.current
      || _.isEmpty(searchConditions)
    ) return;
    (async () => {
      try {
        isSearchingRef.current = true;
        startProgress();
        if (
          _.isEqual(searchConditions, searchProductsConditionsCache)
          && currentPage <= searchProductsPageCache
        ) {
          setSearchResult(searchProductsResultsCache);
          setCurrentPage(searchProductsPageCache);
          setCanLoadMore(searchProductsHasMoreCache);
        } else {
          const data = (await briefSearchProducts({
            ...searchConditions,
            page: currentPage,
          })) || [];
          const hasMore = !_.isEmpty(data);
          setSearchResult((oldSearchResult) => {
            const newResults = [
              ...(oldSearchResult || []),
              ...(data.map(({ key, value }) => {
                const parsedValue = JSON.parse(value);
                addProducts(key, parsedValue);
                return ({ key, value: parsedValue });
              })),
            ];
            setSearchProductsConditionsAndResultsCache(
              searchConditions,
              newResults,
              currentPage,
              hasMore
            );
            return newResults;
          });
          if (!hasMore) setCanLoadMore(false);
        }

        isSearchingRef.current = false;
      } catch (error) {
        console.error(error);
      }
      endProgress();
    })();
  }, [currentPage, searchProductsPageCache, searchProductsHasMoreCache,
    searchConditions, searchProductsConditionsCache, searchProductsResultsCache,
    startProgress, endProgress,
    addProducts, setSearchProductsConditionsAndResultsCache,
  ]);

  useEffect(() => {
    if (!provider || providerDetailsCache[provider]) return;
    (async () => {
      try {
        const providerDetailsData = await findProviderDetails([provider]);
        providerDetailsData.forEach(({ key, value }) => {
          try {
            addProviderDetails(key, JSON.parse(value));
          } catch (error) {
            console.error(error);
          }
        });
      } catch (error2) {
        console.error(error2);
      }
    })();
  }, [provider]); //eslint-disable-line

  useEffect(() => {
    if (!loadMoreDetector.current || !canLoadMore) return undefined;
    const observer = new IntersectionObserver(((entries) => {
      if (!entries[0].isIntersecting || isSearchingRef.current || !canLoadMore) return;
      setCurrentPage((page) => page + 1);
    }), { threshold: [0] });
    observer.observe(loadMoreDetector.current);
    return () => observer.disconnect();
  }, [t, searchResult, canLoadMore]);

  const controllerTitle = useMemo(() => provider
    && [config.BrandNameTW, _.get(providerDetailsCache, [provider, 'name'], '')].filter((d) => d).join(' > '),
  [providerDetailsCache, provider]);
  return (
    <>
      <Navmenu />
      <Wrapper>
        {
          provider && (
          <>
            {
              _.get(providerDetailsCache, [provider, 'banner'], null) && (
                <ProviderBanner src={providerDetailsCache[provider].banner} />
              )
            }
            {
              _.get(providerDetailsCache, [provider, 'notifications'], null) && (
                (providerDetailsCache[provider].notifications || []).map(({
                  text, backgroundColor, textColor,
                }) => (!!text) && (
                  <ProviderNotification color={textColor} backgroundColor={backgroundColor}>
                    {text}
                  </ProviderNotification>
                ))
              )
            }
            <ProviderSideSectionComponent
              provider={provider}
              ref={searchResultHeadPosition}
            />
          </>
          )
        }
        <SearchResultContainer>
          {!provider && searchable && (
            <ProviderSearcher
              searchable={searchable}
              ref={searchResultHeadPosition}
            />
          )}
          <ControllerComponent title={controllerTitle} />
          <SearchProductComponentsContainer productSelectingMode={productSelectingMode}>
            {[
              (searchResult || []).map((productData) => {
                const onClick = (e) => {
                  if (productSelectingMode) {
                    e.stopPropagation();
                    e.preventDefault();
                    window.parent.postMessage({
                      event: 'productSelected',
                      product: productData.key,
                    }, '*');
                  }
                };
                return (
                  <Link
                    to={`/ec/product/${productData.key}`}
                    key={productData.key}
                    onClick={onClick}
                  >
                    <ProductComponent data={productData.value} onClick={onClick} />
                  </Link>
                );
              }),
              [1, 2, 3, 4].map((id) => (
                <span style={{ opacity: 0 }}>
                  <ProductComponent data={undefined} key={id} />
                </span>
              )),
              canLoadMore && <LoadingMoreDetector ref={loadMoreDetector} key="-2" />,
            ]}
          </SearchProductComponentsContainer>
        </SearchResultContainer>
      </Wrapper>
    </>
  );
};
